import React, { useRef, useState } from "react";
import { ImagePlus, X, Loader2, Trash2 } from "lucide-react";
import { compressImage } from "@/lib/image";
import { useT } from "@/i18n";
import CarDamageSchema, { SEVERITY_COLORS } from "@/components/CarDamageSchema";

const MAX_PER_ZONE = 4;
const SEVERITIES = ["light", "medium", "severe"];

// Accident damage editor: clickable car schema + per-zone severity & photos + honesty note.
// zones model: [{ key, severity, photos: [dataUrl] }]. Used in Sell + PRO edit.
export default function DamageInput({ zones = [], note = "", onZones, onNote }) {
  const { t } = useT();
  const list = Array.isArray(zones) ? zones : [];

  const toggle = (key) => {
    const exists = list.some((z) => z.key === key);
    onZones?.(exists ? list.filter((z) => z.key !== key) : [...list, { key, severity: "medium", photos: [] }]);
  };
  const setSeverity = (key, sev) => onZones?.(list.map((z) => (z.key === key ? { ...z, severity: sev } : z)));
  const removeZone = (key) => onZones?.(list.filter((z) => z.key !== key));
  const setPhotos = (key, photos) => onZones?.(list.map((z) => (z.key === key ? { ...z, photos } : z)));

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-400/30 dark:bg-amber-400/[0.06]" data-testid="damage-input">
      <div className="mb-3 text-sm font-bold text-amber-700 dark:text-amber-200">{t("damage.schema.title")}</div>
      <p className="mb-3 text-xs text-amber-700/80 dark:text-amber-200/80">{t("damage.schema.help")}</p>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <CarDamageSchema value={list} onToggle={toggle} />

        <div className="min-w-0 flex-1">
          {list.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-white/40">{t("damage.schema.none")}</p>
          ) : (
            <div className="space-y-2.5" data-testid="damage-zone-cards">
              {list.map((z) => (
                <ZoneCard key={z.key} zone={z} t={t}
                  onSeverity={(s) => setSeverity(z.key, s)}
                  onRemove={() => removeZone(z.key)}
                  onPhotos={(p) => setPhotos(z.key, p)} />
              ))}
            </div>
          )}
        </div>
      </div>

      <textarea data-testid="damage-note" value={note} onChange={(e) => onNote?.(e.target.value)} rows={3}
        placeholder={t("damage.notePlaceholder")}
        className="mt-4 w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-amber-500 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-white/40" />
      <p className="mt-1.5 text-[11px] text-amber-700/70 dark:text-amber-200/70">{t("damage.hint")}</p>
    </div>
  );
}

function ZoneCard({ zone, t, onSeverity, onRemove, onPhotos }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const photos = zone.photos || [];

  const add = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const room = Math.max(0, MAX_PER_ZONE - photos.length);
      const next = [];
      for (const f of files.slice(0, room)) next.push(await compressImage(f, 900, 0.8));
      onPhotos([...photos, ...next]);
    } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-white/[0.03]" data-testid={`damage-card-${zone.key}`}>
      <div className="flex items-center gap-2">
        <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: SEVERITY_COLORS[zone.severity || "medium"] }} />
        <span className="flex-1 truncate text-sm font-bold text-slate-800 dark:text-white">{t(`damage.zone.${zone.key}`)}</span>
        <button type="button" onClick={onRemove} data-testid={`damage-card-remove-${zone.key}`}
          className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition hover:bg-rose-500/10 hover:text-rose-500">
          <Trash2 size={14} />
        </button>
      </div>
      {/* severity segmented */}
      <div className="mt-2 flex gap-1 rounded-lg bg-slate-100 p-1 dark:bg-white/5">
        {SEVERITIES.map((s) => {
          const active = (zone.severity || "medium") === s;
          return (
            <button key={s} type="button" onClick={() => onSeverity(s)} data-testid={`damage-sev-${zone.key}-${s}`}
              className={`flex-1 rounded-md px-2 py-1 text-[11px] font-bold transition ${active ? "text-white" : "text-slate-500 hover:text-slate-700 dark:text-white/50 dark:hover:text-white"}`}
              style={active ? { background: SEVERITY_COLORS[s] } : undefined}>
              {t(`damage.severity.${s}`)}
            </button>
          );
        })}
      </div>
      {/* per-zone photos */}
      <div className="mt-2 flex flex-wrap gap-2">
        {photos.map((src, i) => (
          <div key={i} className="relative h-14 w-14 overflow-hidden rounded-lg border border-slate-200 dark:border-white/10">
            <img src={src} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => onPhotos(photos.filter((_, idx) => idx !== i))} data-testid={`damage-photo-remove-${zone.key}-${i}`}
              className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white"><X size={11} /></button>
          </div>
        ))}
        {photos.length < MAX_PER_ZONE && (
          <button type="button" data-testid={`damage-photo-add-${zone.key}`} onClick={() => ref.current?.click()} disabled={busy}
            className="flex h-14 w-14 flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-amber-400/60 text-amber-600 transition hover:bg-amber-400/10 dark:text-amber-300">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
            <span className="text-[8px] font-bold">{t("damage.addPhoto")}</span>
          </button>
        )}
        <input ref={ref} type="file" accept="image/*" multiple onChange={add} className="hidden" />
      </div>
    </div>
  );
}
